import { Row, Table } from "@tanstack/react-table";
import { DocumentsState } from "./types";
import { RoleBasedPermissionsFeature } from "@src/auth/permissions/permissionsClaims";

export const EQUIPMENT_GROUP_ID = -1;
export const EQUIPMENT_GROUP_NAME = "Equipment";
export const EQUIPMENT_CATEGORY_ID = "equipment" as const;

export const UTILITY_BILL_GROUP_ID = -2;
export const UTILITY_BILL_GROUP_NAME = "Utility Bills";
export const UTILITY_BILL_CATEGORY_ID = "utility_bill" as const;

export const PROJECT_DOCUMENT_GROUP_ID = -3;
export const PROJECT_DOCUMENT_GROUP_NAME = "Projects";
export const PROJECT_DOCUMENT_CATEGORY_ID = "project" as const;

export function getNonStandardDocsSelected(table: Table<DocumentsState>) {
  const selectedNonStandardDocsMap = {
    Equipment: table
      .getFilteredSelectedRowModel()
      .rows.some((row) => isEquipmentDocument(row.original)),
    "Utility Bills": table
      .getFilteredSelectedRowModel()
      .rows.some((row) => isUtilityBill(row.original)),
    Project: table
      .getFilteredSelectedRowModel()
      .rows.some((row) => isProjectDocument(row.original)),
  };

  const selectedNonStandardDocs = Object.entries(selectedNonStandardDocsMap)
    .filter(([_, value]) => value)
    .map(([key]) => key) as (keyof typeof selectedNonStandardDocsMap)[];

  if (selectedNonStandardDocs.length === 0) {
    return false;
  }
  if (selectedNonStandardDocs.length <= 2) {
    return selectedNonStandardDocs.join(" or ");
  }
  if (selectedNonStandardDocs.length === 3) {
    return "Equipment, Utility Bills, or Project Documents";
  }
}

export function getDesignDocUtilityBillSelected(table: Table<DocumentsState>) {
  return table
    .getFilteredSelectedRowModel()
    .rows.some(
      (row) =>
        row.original.category === UTILITY_BILL_CATEGORY_ID &&
        row.original.isDesignDoc
    )
    ? "Utility Bills"
    : false;
}

function isUtilityBill(doc: DocumentsState | null | undefined) {
  return doc?.category === UTILITY_BILL_CATEGORY_ID && !doc?.isDesignDoc;
}

function isEquipmentDocument(doc: DocumentsState | null | undefined) {
  return doc?.category === EQUIPMENT_CATEGORY_ID;
}

function isProjectDocument(doc: DocumentsState | null | undefined) {
  return doc?.category === PROJECT_DOCUMENT_CATEGORY_ID;
}

export function isDesignDocWithUtilityBillCategory(
  doc: DocumentsState | null | undefined
) {
  return doc?.category === UTILITY_BILL_CATEGORY_ID && doc?.isDesignDoc;
}

export function isNonStandardDocumentGroupId(groupId?: number) {
  if (!groupId) return false;
  return [
    EQUIPMENT_GROUP_ID,
    UTILITY_BILL_GROUP_ID,
    PROJECT_DOCUMENT_GROUP_ID,
  ].includes(groupId);
}

export function getDocumentType(doc: DocumentsState | null | undefined) {
  if (isEquipmentDocument(doc)) {
    return "equipment";
  }
  if (isUtilityBill(doc)) {
    return "utilityBill";
  }
  if (isProjectDocument(doc)) {
    return "project";
  }
  return "design";
}

export function splitDocumentTypes(rows: Row<DocumentsState>[]) {
  return rows.reduce(
    (acc, x) => {
      const type = getDocumentType(x.original);
      return { ...acc, [type]: [...acc[type], x.original.id] };
    },
    {
      design: [] as number[],
      equipment: [] as number[],
      utilityBill: [] as number[],
      project: [] as number[],
    }
  );
}

export const DOCUMENT_TYPE_FEATURE_ACCESS_MAP: Record<
  ReturnType<typeof getDocumentType>,
  RoleBasedPermissionsFeature
> = {
  design: "Documents",
  equipment: "Equipment",
  project: "Projects",
  utilityBill: "Utilities",
};
